<template>
    <div>
        <apply-ticket-note-template-modal @apply-template="applyTemplate"></apply-ticket-note-template-modal>
        <loading-screen :is-loading="isSaving"></loading-screen>
        <errors :error-list="serverErrors"></errors>
        <!-- Description -->
        <div class="card shadow-none card-action">
            <div class="card-header">
                <div class="avatar-xxs d-inline-block mr-3">
                            <span class="avatar-title rounded bg-soft-primary text-primary font-size-14">
                                {{ $auth.user.name.charAt(0) }}
                            </span>
                </div>
                <b class="d-inline-block">Create Note</b>
                
                <!-- Include in Incident Report Checkbox - only show if there's an incident report -->
                <div v-if="incidentReport" class="float-right" style="margin-top: 4px;">
                    <div class="form-check d-flex align-items-center">
                        <input
                            type="checkbox"
                            id="include_in_incident_report"
                            v-model="includeInIncidentReport"
                            class="form-check-input"
                            style="
                                margin-right: 3px;
                                margin-top: 0;
                                vertical-align: middle;
                                position: relative;
                                top: -1px;
                            "
                        />
                        <label class="form-check-label mb-0" style="
                            font-weight: 500;
                            color: #495057;
                            transition: color 0.2s ease;
                            line-height: 1.2;
                            vertical-align: middle;
                        " for="include_in_incident_report">
                            <i class="fa fa-file-text-o mr-1" style="color: #dc3545;"></i>
                            Include in incident report
                        </label>
                    </div>
                </div>
            </div>
            <div class="card-body pb-0">
                <editor-content :editor="editor"/>
                <!-- Suggestions -->
                <div class="suggestion-list" v-show="showSuggestions" ref="suggestions">
                    <template v-if="hasResults">
                        <div
                                v-for="(user, index) in filteredUsers"
                                :key="user.id"
                                class="suggestion-list__item"
                                :class="{ 'is-selected': navigatedUserIndex === index }"
                                @click="selectUser(user)"
                        >
                            {{ user.name }}
                        </div>
                    </template>
                    <div v-else class="suggestion-list__item is-empty">
                        No users found
                    </div>
                </div>
                <!-- / Suggestions -->
            </div>
            
            <!-- Incident Report Timestamp Section - Subtle -->
            <div v-if="incidentReport && includeInIncidentReport" class="incident-timestamp-section" style="
                background: #f8f9fa;
                border-top: 1px solid #e9ecef;
                padding: 12px 16px;
                margin: 0 -1px;
                animation: slideInFromTop 0.3s ease-out;
            ">
                <div class="d-flex align-items-center justify-content-between">
                    <div class="d-flex align-items-center">
                        <!-- Timestamp Field -->
                        <div class="d-flex align-items-center mr-4">
                            <label for="incident_report_timestamp" class="form-label mb-0 mr-2" style="
                                font-size: 12px; 
                                color: #6c757d; 
                                font-weight: 500;
                                margin-bottom: 0;
                            ">
                                <i class="fa fa-clock-o mr-1" style="color: #6c757d;"></i>
                                Override timestamp:
                            </label>
                            <input
                                type="datetime-local"
                                id="incident_report_timestamp"
                                v-model="incidentReportTimestamp"
                                class="form-control form-control-sm"
                                style="
                                    border: 1px solid #ced4da;
                                    border-radius: 4px;
                                    font-size: 12px;
                                    padding: 4px 8px;
                                    background: white;
                                    width: 180px;
                                    transition: border-color 0.2s ease;
                                "
                                @focus="$event.target.style.borderColor = '#007bff'"
                                @blur="$event.target.style.borderColor = '#ced4da'"
                            />
                        </div>
                        
                        <!-- Timezone Field -->
                        <div class="d-flex align-items-center">
                            <label for="timezone_select" class="form-label mb-0 mr-2" style="
                                font-size: 12px; 
                                color: #6c757d; 
                                font-weight: 500;
                                margin-bottom: 0;
                            ">
                                <i class="fa fa-globe mr-1" style="color: #6c757d;"></i>
                                Timezone:
                            </label>
                            <select
                                id="timezone_select"
                                v-model="selectedTimezone"
                                class="form-control form-control-sm"
                                style="
                                    border: 1px solid #ced4da;
                                    border-radius: 4px;
                                    font-size: 12px;
                                    padding: 4px 8px;
                                    background: white;
                                    width: 240px;
                                    transition: border-color 0.2s ease;
                                "
                                @focus="$event.target.style.borderColor = '#007bff'"
                                @blur="$event.target.style.borderColor = '#ced4da'"
                            >
                                <option 
                                    v-for="tz in timezones" 
                                    :key="tz.value" 
                                    :value="tz.value"
                                    :disabled="tz.disabled"
                                >
                                    {{ tz.flag }} {{ tz.label }}
                                </option>
                            </select>
                        </div>
                    </div>
                    <small class="text-muted" style="font-size: 10px;">
                        Optional: Override note creation time
                    </small>
                </div>
            </div>
            <!-- / Incident Report Timestamp Section -->
            
            <div class="card-menubar">
                <editor-menu-bar :editor="editor" v-slot="{ commands, isActive }">
                    <div class="menubar">

                        <a href="#" class="btn btn-itg-white btn-sm menubar__button"
                           :class="{ 'is-active': isActive.bold() }"
                           @click="commands.bold"
                        >
                            <i class="fas fa-bold"></i>
                        </a>

                        <a href="#" class="btn btn-itg-white btn-sm menubar__button"
                           :class="{ 'is-active': isActive.italic() }"
                           @click="commands.italic"
                        >
                            <i class="fas fa-italic"></i>
                        </a>

                        <a href="#" class="btn btn-itg-white btn-sm menubar__button"
                           :class="{ 'is-active': isActive.strike() }"
                           @click="commands.strike"
                        >
                            <i class="fas fa-strikethrough"></i>
                        </a>

                        <a href="#" class="btn btn-itg-white btn-sm menubar__button"
                           :class="{ 'is-active': isActive.underline() }"
                           @click="commands.underline"
                        >
                            <i class="fas fa-underline"></i>
                        </a>

                        <a href="#" class="btn btn-itg-white btn-sm menubar__button"
                           :class="{ 'is-active': isActive.bullet_list() }"
                           @click="commands.bullet_list"
                        >
                            <i class="fas fa-list-ul"></i>
                        </a>

                        <a href="#" class="btn btn-itg-white btn-sm menubar__button"
                           :class="{ 'is-active': isActive.ordered_list() }"
                           @click="commands.ordered_list"
                        >
                            <i class="fas fa-list-ol"></i>
                        </a>

                        <a href="#" class="btn btn-itg-white btn-sm menubar__button"
                           @click="commands.undo"
                        >
                            <i class="fas fa-undo"></i>
                        </a>

                        <a href="#" class="btn btn-itg-white btn-sm menubar__button"
                           @click="commands.redo"
                        >
                            <i class="fas fa-redo"></i>
                        </a>

                        <a @click.prevent="openMediaUploader"
                         href="#" class="btn btn-itg-white btn-sm menubar__button">
                            <i class="fas fa-images"></i>
                        </a>
                        <upload-media-modal @file-uploaded="r => handleFileUploaded(r, commands.image)"
                        :ticket-id="ticket.id" modal-id="create-ticket-note-upload-media-modal"></upload-media-modal>

                        <a href="#" class="btn btn-itg-white btn-sm menubar__button"
                           @click="openApplyTicketNoteTemplateModal"
                        >
                            <i class="fas fa-paste"></i> Template
                        </a>
                    </div>
                </editor-menu-bar>
            </div>
            <div class="card-footer text-right">
                <button type="button" class="btn btn-itg mr-2" @click="attemptCancel">
                    Cancel
                </button>

                <button type="button" class="btn btn-itg-dark" @click="saveNote">
                    Add note
                </button>
            </div>
        </div>
        <!-- / Description -->

    </div>
</template>

<script>
    import {mapGetters, mapActions} from "vuex";
    import Forms from "../../../../../mixins/Forms";
    import Fuse from 'fuse.js';
    import tippy, {sticky} from 'tippy.js';
    import ApplyTicketNoteTemplateModal from "./CreateTicketNote/ApplyTicketNoteTemplateModal";
    import UploadMediaModal from './UploadMediaModal.vue';
    import axios from 'axios';

    import {Editor, EditorContent, EditorMenuBar} from 'tiptap';
    import {
        Blockquote,
        CodeBlock,
        HardBreak,
        Heading,
        OrderedList,
        BulletList,
        ListItem,
        TodoItem,
        TodoList,
        Bold,
        Code,
        Italic,
        Link,
        Strike,
        Underline,
        History, Mention,
        Image
    } from 'tiptap-extensions';

    export default {
        name: "CreateTicketNote",
        data() {
            return {
                isSaving: false,
                serverErrors: [],
                value: null,
                editor: null,

                query: null,
                suggestionRange: null,
                filteredUsers: [],
                navigatedUserIndex: 0,
                insertMention: () => {
                },
                includeInIncidentReport: false,
                incidentReportTimestamp: null,
                selectedTimezone: null,
                timezones: [
                    // Key timezones at the top
                    { value: 'Europe/London', label: 'London (GMT/BST)', flag: '🇬🇧' },
                    { value: 'Asia/Tokyo', label: 'Japan (JST)', flag: '🇯🇵' },
                    { value: 'Australia/Sydney', label: 'Australia (AEST/AEDT)', flag: '🇦🇺' },
                    { value: 'America/New_York', label: 'US Eastern (EST/EDT)', flag: '🇺🇸' },
                    { value: 'America/Chicago', label: 'US Central (CST/CDT)', flag: '🇺🇸' },
                    { value: 'America/Denver', label: 'US Mountain (MST/MDT)', flag: '🇺🇸' },
                    { value: 'America/Los_Angeles', label: 'US Pacific (PST/PDT)', flag: '🇺🇸' },
                    { value: 'America/Detroit', label: 'US Detroit (EST/EDT)', flag: '🇺🇸' },
                    // Separator
                    { value: '', label: '──────────────', flag: '', disabled: true },
                    // Other common timezones
                    { value: 'Europe/Paris', label: 'Paris (CET/CEST)', flag: '🇫🇷' },
                    { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)', flag: '🇩🇪' },
                    { value: 'Asia/Shanghai', label: 'Shanghai (CST)', flag: '🇨🇳' },
                    { value: 'Asia/Singapore', label: 'Singapore (SGT)', flag: '🇸🇬' },
                    { value: 'Asia/Dubai', label: 'Dubai (GST)', flag: '🇦🇪' },
                    { value: 'America/Toronto', label: 'Toronto (EST/EDT)', flag: '🇨🇦' },
                    { value: 'America/Vancouver', label: 'Vancouver (PST/PDT)', flag: '🇨🇦' },
                    { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)', flag: '🇳🇿' }
                ]
            }
        },

        beforeDestroy() {
            this.destroyPopup()
        },

        computed: {
            ...mapGetters('ticketViewer', {
                ticket: 'ticket',
                agents: 'agents',
                incidentReport: 'incidentReport'
            }),

            hasResults() {
                return this.filteredUsers.length;
            },
            showSuggestions() {
                return this.query || this.hasResults;
            }
        },
        
        watch: {
            includeInIncidentReport(newVal) {
                if (newVal && !this.selectedTimezone) {
                    // Set timezone when incident report section becomes visible
                    this.setDefaultTimezone();
                }
            }
        },
        mounted() {
            this.setupEditor();
            this.setDefaultTimezone();
            
            // Also try to set timezone after a short delay in case window.itgTimezone is set later
            setTimeout(() => {
                if (!this.selectedTimezone || this.selectedTimezone === 'Europe/London') {
                    this.setDefaultTimezone();
                }
            }, 100);
        },
        components: {EditorMenuBar, EditorContent, ApplyTicketNoteTemplateModal, UploadMediaModal},
        mixins: [Forms],
        methods: {
            ...mapActions('ticketViewer', {
                cancelAction: 'cancelAction',
                actionComplete: 'actionComplete',
                loadMedia: 'loadMedia'
            }),

            saveNote() {
                let vm = this;
                let editorHtml = vm.editor.getHTML();
                let idsOfUsersMentioned = this.getMentionIdsFromHtml(editorHtml);


                vm.isSaving = true;
                vm.serverErrors = [];
                axios.post(API_BASE + '/tickets/' + this.ticket.id + '/notes', {
                    body: editorHtml,
                    ids_of_users_mentioned: idsOfUsersMentioned,
                    include_in_incident_report: this.incidentReport ? this.includeInIncidentReport : false,
                    incident_report_timestamp: this.incidentReport && this.includeInIncidentReport ? this.convertToUtc(this.incidentReportTimestamp) : null
                }).then(r => {
                    vm.isSaving = false;
                    this.actionComplete();
                }).catch(e => {
                    vm.isSaving = false;
                    vm.setErrors(e);
                });
            },

            attemptCancel() {
                let vm = this;
                swal({
                    title: "Are you sure you want to cancel?",
                    text: "You have unsaved changes and your data will be lost.",
                    icon: "warning",
                    buttons: {
                        cancel: true,
                        confirm: "Leave without saving"
                    },
                    dangerMode: true,
                }).then((shouldCancel) => {
                    if (shouldCancel) {
                        vm.cancelAction();
                    }
                });
            },

            setupEditor() {
                let vm = this;
                this.editor = new Editor({
                    extensions: [
                        new Blockquote(),
                        new CodeBlock(),
                        new HardBreak(),
                        new Heading({levels: [1, 2, 3]}),
                        new BulletList(),
                        new OrderedList(),
                        new ListItem(),
                        new TodoItem(),
                        new TodoList(),
                        new Bold(),
                        new Code(),
                        new Italic(),
                        new Link(),
                        new Strike(),
                        new Underline(),
                        new History(),
                        new Image(),
                        new Mention({
                            // a list of all suggested items
                            items: async () => {
                                await new Promise(resolve => {
                                    setTimeout(resolve, 500)
                                })
                                return vm.agents;
                            },
                            // is called when a suggestion starts
                            onEnter: ({
                                          items, query, range, command, virtualNode,
                                      }) => {
                                this.query = query
                                this.filteredUsers = items
                                this.suggestionRange = range
                                this.renderPopup(virtualNode)
                                // we save the command for inserting a selected mention
                                // this allows us to call it inside of our custom popup
                                // via keyboard navigation and on click
                                this.insertMention = command
                            },
                            // is called when a suggestion has changed
                            onChange: ({
                                           items, query, range, virtualNode,
                                       }) => {
                                this.query = query
                                this.filteredUsers = items
                                this.suggestionRange = range
                                this.navigatedUserIndex = 0
                                this.renderPopup(virtualNode)
                            },
                            // is called when a suggestion is cancelled
                            onExit: () => {
                                // reset all saved values
                                this.query = null
                                this.filteredUsers = []
                                this.suggestionRange = null
                                this.navigatedUserIndex = 0
                                this.destroyPopup()
                            },
                            // is called on every keyDown event while a suggestion is active
                            onKeyDown: ({event}) => {
                                if (event.key === 'ArrowUp') {
                                    this.upHandler()
                                    return true
                                }
                                if (event.key === 'ArrowDown') {
                                    this.downHandler()
                                    return true
                                }
                                if (event.key === 'Enter') {
                                    this.enterHandler()
                                    return true
                                }
                                return false
                            },
                            // is called when a suggestion has changed
                            // this function is optional because there is basic filtering built-in
                            // you can overwrite it if you prefer your own filtering
                            // in this example we use fuse.js with support for fuzzy search
                            onFilter: async (items, query) => {
                                if (!query) {
                                    return items
                                }
                                await new Promise(resolve => {
                                    setTimeout(resolve, 500)
                                })
                                const fuse = new Fuse(items, {
                                    threshold: 0.2,
                                    keys: ['name'],
                                })
                                return fuse.search(query).map(item => item.item)
                            },
                        })
                    ],
                    content: this.value,
                    autoFocus: true,
                    onInit: function () {
                        vm.$nextTick(function () {
                            window.setTimeout(function () {
                                let container = document.getElementById('ticket-main-section-left-column');
                                container.scrollTo({
                                    top: container.scrollHeight,
                                    behavior: "smooth",
                                    cancelable: false
                                });
                            }, 1);
                        });
                    }
                })
            },

            openApplyTicketNoteTemplateModal() {
                this.$bvModal.show('apply-ticket-note-template-modal');
            },

            applyTemplate(template) {
                this.$bvModal.hide('apply-ticket-note-template-modal');
                this.editor.setContent(template.body);
            },

            // navigate to the previous item
            // if it's the first item, navigate to the last one
            upHandler() {
                this.navigatedUserIndex = ((this.navigatedUserIndex + this.filteredUsers.length) - 1) % this.filteredUsers.length
            },
            // navigate to the next item
            // if it's the last item, navigate to the first one
            downHandler() {
                this.navigatedUserIndex = (this.navigatedUserIndex + 1) % this.filteredUsers.length
            },
            enterHandler() {
                const user = this.filteredUsers[this.navigatedUserIndex]
                if (user) {
                    this.selectUser(user)
                }
            },
            // we have to replace our suggestion text with a mention
            // so it's important to pass also the position of your suggestion text
            selectUser(user) {
                this.insertMention({
                    range: this.suggestionRange,
                    attrs: {
                        id: user.id,
                        label: user.name,
                    },
                })
                this.editor.focus()
            },
            // renders a popup with suggestions
            // tiptap provides a virtualNode object for using popper.js (or tippy.js) for popups
            renderPopup(node) {
                const boundingClientRect = node.getBoundingClientRect()
                const {x, y} = boundingClientRect
                if (x === 0 && y === 0) {
                    return
                }
                if (this.popup) {
                    return
                }
                // ref: https://atomiks.github.io/tippyjs/v6/all-props/
                this.popup = tippy('#app', {
                    getReferenceClientRect: () => boundingClientRect,
                    appendTo: () => document.body,
                    interactive: true,
                    sticky: true, // make sure position of tippy is updated when content changes
                    plugins: [sticky],
                    content: this.$refs.suggestions,
                    trigger: 'mouseenter', // manual
                    showOnCreate: true,
                    theme: 'dark',
                    placement: 'top-start',
                    inertia: true,
                    duration: [400, 200],
                })
            },
            destroyPopup() {
                if (this.popup) {
                    this.popup[0].destroy()
                    this.popup = null
                }
            },
            openMediaUploader() {
                this.$bvModal.show('create-ticket-note-upload-media-modal');
            },
            handleFileUploaded(r, imgCommand) {
                this.loadMedia();
                let url = r['url'];
                this.$bvModal.hide('create-ticket-note-upload-media-modal');
                if (url) {
                    imgCommand({src: url});
                }
            },

            // Set default timezone
            setDefaultTimezone() {
                // Check if window.itgTimezone exists and is in our timezone list
                const systemTimezone = window.itgTimezone;
                console.log('System timezone:', systemTimezone);
                
                if (systemTimezone) {
                    // Check if the system timezone is in our list
                    const timezoneExists = this.timezones.some(tz => tz.value === systemTimezone);
                    if (timezoneExists) {
                        this.selectedTimezone = systemTimezone;
                        console.log('Using system timezone:', systemTimezone);
                        return;
                    }
                }
                
                // Fallback to London if system timezone not found in our list
                this.selectedTimezone = 'Europe/London';
                console.log('Using fallback timezone: Europe/London');
            },

            // Timezone conversion method
            convertToUtc(localDateTime) {
                if (!localDateTime) return null;
                const timezone = this.selectedTimezone || window.itgTimezone || 'UTC';
                return moment.tz(localDateTime, timezone).utc().format('YYYY-MM-DD HH:mm:ss');
            }
        }
    }
</script>

<style scoped>
    .card {
        background-color: #f5f7f9;
    }
    
    /* Subtle animation for timestamp section appearance */
    .incident-timestamp-section {
        animation: slideInFromTop 0.2s ease-out;
    }
    
    @keyframes slideInFromTop {
        from {
            opacity: 0;
            transform: translateY(-5px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
    
    /* Enhanced checkbox styling */
    #include_in_incident_report:checked + label {
        color: #dc3545 !important;
        font-weight: 600;
    }
    
    #include_in_incident_report:checked + label i {
        color: #dc3545 !important;
    }
    
    /* Focus state for the input */
    #incident_report_timestamp:focus {
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        border-color: #007bff;
    }
    
    /* Focus state for the timezone select */
    #timezone_select:focus {
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        border-color: #007bff;
    }
    
    /* Disabled option styling */
    #timezone_select option:disabled {
        color: #6c757d;
        font-style: italic;
    }
</style>
